import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import TradeDrawer from "./TradeDrawer";
import { MoneyDrawer } from "./CryptoTab";

/* ===== Events — one time-sorted feed of every trade lifecycle event =====
   Indian (fib-vwap books) + Crypto, derived purely from the trade records'
   own timestamps: ENTRY (trade placed), TIER (partial booked), EXIT (SL /
   target / trail / EOD...). This is also the "what just beeped?" answer —
   the notification sound fires on exactly these transitions. */

const rup = (v) => v == null ? "" : `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
const when = (v) => new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
const ago = (ts) => {
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 90) return `${Math.round(s)}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${(s / 3600).toFixed(1)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const KIND_META = {
  ENTRY: { icon: "🆕", color: "#3b82f6" },
  TIER: { icon: "🏦", color: "#10b981" },
  EXIT_WIN: { icon: "🎯", color: "#34d399" },
  EXIT_LOSS: { icon: "🛑", color: "#f87171" },
  EXIT_FLAT: { icon: "🏁", color: "#9ca3af" },
};

function cryptoBook(t) {
  return t.book === "OB" ? "1h OB-retest"
    : t.side === "SHORT" ? "4h short"
    : String(t.symbol).startsWith("PAXG") || String(t.symbol).startsWith("XAU") ? "4h gold"
    : t.tf === "1d" ? "1d trail" : `${t.tf} ST-trail`;
}

/* derive events from both books' trade records */
function deriveEvents(indian, crypto) {
  const ev = [];
  let cur = null;
  const push = (ts, source, kind, title, detail, pnl, live) => {
    const t = Date.parse(ts);
    if (!isNaN(t)) ev.push({ ts: t, source, kind, title, detail, pnl, live, trade: cur });
  };
  for (const t of indian) {
    cur = t;
    const label = t.strategy === "orb-gap" ? `${t.index} ORB-Gap ${t.type}` : `${t.index || "?"} ${t.type || ""} ${t.fibLevel || ""}`.trim();
    const live = t.simulated === false;
    if (t.time) push(t.time, "Indian", "ENTRY", label, `${t.tradingsymbol || ""} · entry ${t.entry ?? ""}${t.entryPremium ? ` @ ₹${t.entryPremium}` : ""}`, null, live);
    if (t.trailExitTime || t.trailExitDate) {
      push(t.trailExitTime || t.trailExitDate, "Indian", "TIER", label,
        `${t.tier1Done && t.tier2Done ? "both tiers" : t.tier1Done ? "tier 1" : t.tier2Done ? "tier 2" : "partial"} booked: ${t.trailExitQty ?? ""} @ ₹${t.trailExitPremium ?? "?"}`, t.trailExitPnl, live);
    }
    if (t.exitTime) {
      const pnl = t.netPnl;
      const kind = pnl > 0 ? "EXIT_WIN" : pnl < 0 ? "EXIT_LOSS" : "EXIT_FLAT";
      push(t.exitTime, "Indian", kind, label, `${t.exitReason || "closed"}${t.exitPremium ? ` @ ₹${t.exitPremium}` : ""}`, pnl, live);
    }
  }
  for (const t of crypto) {
    cur = t;
    const label = `${t.ref ? t.ref + ' · ' : ''}${t.symbol} ${cryptoBook(t)}`;
    if (t.entry_time) push(t.entry_time, "Crypto", "ENTRY", label, `${t.side || "LONG"} · entry ${t.entry}`, null, false);
    if (t.tierTime) push(t.tierTime, "Crypto", "TIER", label, `bank @ ${t.tierPrice ?? ""} · stop → BE (risk-free)`, null, false);
    if (t.bank2Time) push(t.bank2Time, "Crypto", "TIER", label, `2nd bank @ ${t.bank2Price ?? ""} · total banked ₹${Math.round(t.bookedPnl || 0).toLocaleString("en-IN")}`, t.bookedPnl, false);
    if (t.exit_time) {
      const pnl = t.pnl_inr;
      const kind = pnl > 0 ? "EXIT_WIN" : pnl < 0 ? "EXIT_LOSS" : "EXIT_FLAT";
      push(t.exit_time, "Crypto", kind, label, `${t.exit_reason || "closed"} @ ${t.exit_price ?? ""}${t.r_mult != null ? ` (${t.r_mult >= 0 ? "+" : ""}${t.r_mult}R)` : ""}`, pnl, false);
    }
  }
  return ev.sort((a, b) => b.ts - a.ts);
}

const chip = (active) => ({
  fontSize: 11, padding: "3px 10px", borderRadius: 14, cursor: "pointer",
  border: active ? "1px solid #8b5cf6" : "1px solid var(--panel3)",
  background: active ? "#5b21b6" : "var(--chart-bg)",
  color: active ? "#fff" : "var(--muted2)", fontWeight: active ? 700 : 500,
});

export default function EventsTab() {
  const [indian, setIndian] = useState([]);
  const [crypto, setCrypto] = useState([]);
  const [orb, setOrb] = useState([]);
  const [cst, setCst] = useState(null);   // full crypto status for the MoneyDrawer
  const [detail, setDetail] = useState(null); // {source, trade}
  const [inErr, setInErr] = useState("");
  const [period, setPeriod] = useState("TODAY");
  const [fSrc, setFSrc] = useState("ALL");
  const [fKind, setFKind] = useState("ALL");
  // events newer than the last visit glow — this answers "what just beeped?"
  const [seenTs] = useState(() => { try { return Number(localStorage.getItem("eventsSeenTs") || 0); } catch { return 0; } });
  useEffect(() => { try { localStorage.setItem("eventsSeenTs", String(Date.now())); } catch {} }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get("/api/zerodha/auto-trade/status", { timeout: 15000 });
        if (r.data?.trades) { setIndian(r.data.trades.filter(t => String(t.strategy || "").startsWith("fib-vwap"))); setInErr(""); }
        else setInErr(r.data?.error || "");
      } catch (e) { setInErr(e.message); }
      try {
        const o = await axios.get("/api/zerodha/orb-gap/status", { timeout: 15000 });
        if (o.data?.success) setOrb([...(o.data.open || []), ...(o.data.closed || [])]);
      } catch {}
      try {
        const c = await axios.get("/api/crypto/trader/status", { timeout: 15000 });
        if (c.data?.success) { setCst(c.data); setCrypto([...(c.data.openTrades || []), ...(c.data.closedTrades || [])]); }
      } catch {}
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const events = useMemo(() => deriveEvents([...indian, ...orb], crypto), [indian, orb, crypto]);

  const filtered = useMemo(() => {
    let cutoff = 0;
    if (period === "TODAY") { const d = new Date(); d.setHours(0, 0, 0, 0); cutoff = d.getTime(); }
    else if (period !== "ALL") cutoff = Date.now() - parseInt(period, 10) * 86400000;
    return events.filter(e =>
      e.ts >= cutoff &&
      (fSrc === "ALL" || e.source === fSrc) &&
      (fKind === "ALL" || (fKind === "EXIT" ? e.kind.startsWith("EXIT") : e.kind === fKind)));
  }, [events, period, fSrc, fKind]);

  const newCount = events.filter(e => e.ts > seenTs).length;

  return (
    <div>
      <div className="dashboard-header">
        <div>
          <h2>Events <span style={{ fontSize: 12, color: "var(--muted2)", fontWeight: 400 }}>every entry, tier booking and exit — Indian + crypto, newest first</span></h2>
          <p className="dashboard-subtitle">
            The notification beep fires on exactly these transitions — when you hear one, the top row here is what it was.
            Rows since your last visit are highlighted{newCount ? ` (${newCount} new)` : ""}.
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 10.5, color: "var(--muted2)" }}>Period:</span>
        {[["TODAY", "Today"], ["7", "7D"], ["30", "30D"], ["ALL", "All"]].map(([v, l]) =>
          <button key={v} style={chip(period === v)} onClick={() => setPeriod(v)}>{l}</button>)}
        <span style={{ fontSize: 10.5, color: "var(--muted2)", marginLeft: 10 }}>Source:</span>
        {["ALL", "Indian", "Crypto"].map(v => <button key={v} style={chip(fSrc === v)} onClick={() => setFSrc(v)}>{v}</button>)}
        <span style={{ fontSize: 10.5, color: "var(--muted2)", marginLeft: 10 }}>Kind:</span>
        {[["ALL", "All"], ["ENTRY", "🆕 Entries"], ["TIER", "🏦 Tier bookings"], ["EXIT", "🏁 Exits"]].map(([v, l]) =>
          <button key={v} style={chip(fKind === v)} onClick={() => setFKind(v)}>{l}</button>)}
        <span style={{ marginLeft: "auto", fontSize: 10.5, color: "var(--muted2)" }}>
          {filtered.length} events{inErr ? " · ⚠️ Indian book unavailable (Kite not logged in?)" : ""}
        </span>
      </div>

      <div className="tl-table-wrap">
        <table className="stocks-table" style={{ width: "100%" }}>
          <thead><tr><th>When</th><th></th><th>Event</th><th>Trade</th><th>Detail</th><th>P&L</th><th>Mode</th></tr></thead>
          <tbody>
            {filtered.map((e, i) => {
              const m = KIND_META[e.kind] || KIND_META.EXIT_FLAT;
              const isNew = e.ts > seenTs;
              return (
                <tr key={i} onClick={() => setDetail({ source: e.source, trade: e.trade })}
                  style={{ cursor: "pointer", ...(isNew ? { background: "color-mix(in srgb, #8b5cf6 12%, transparent)" } : {}) }}
                  title="Click for the full trade details">
                  <td style={{ whiteSpace: "nowrap" }} title={when(e.ts)}>{when(e.ts)}<div style={{ fontSize: 9.5, color: "var(--muted2)" }}>{ago(e.ts)}{isNew ? " · NEW" : ""}</div></td>
                  <td style={{ fontSize: 15 }}>{m.icon}</td>
                  <td style={{ fontWeight: 700, color: m.color, whiteSpace: "nowrap" }}>
                    {e.kind === "ENTRY" ? "ENTRY" : e.kind === "TIER" ? "TIER BOOKED" : "EXIT"}
                    <div style={{ fontSize: 9, fontWeight: 400, color: "var(--muted2)" }}>{e.source}</div>
                  </td>
                  <td style={{ fontWeight: 600 }}>{e.title}</td>
                  <td style={{ fontSize: 11.5, color: "var(--dim2)" }}>{e.detail}</td>
                  <td className={e.pnl == null ? "" : e.pnl >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{rup(e.pnl)}</td>
                  <td>{e.source === "Indian"
                    ? <span style={{ fontSize: 8.5, fontWeight: 800, padding: "1px 5px", borderRadius: 3, background: e.live ? "#dc2626" : "var(--btn-muted)", color: e.live ? "#fff" : "var(--muted2)", border: e.live ? "none" : "1px solid var(--btn-border)" }}>{e.live ? "LIVE" : "PAPER"}</span>
                    : <span style={{ fontSize: 8.5, fontWeight: 800, padding: "1px 5px", borderRadius: 3, background: "var(--btn-muted)", color: "var(--muted2)", border: "1px solid var(--btn-border)" }}>PAPER</span>}</td>
                </tr>
              );
            })}
            {!filtered.length && <tr><td colSpan={7} style={{ color: "var(--muted2)" }}>No events in this window{inErr ? " — the Indian book needs Kite login; crypto events appear regardless" : ""}.</td></tr>}
          </tbody>
        </table>
      </div>
      {detail && detail.source === "Indian" && <TradeDrawer trade={detail.trade} onClose={() => setDetail(null)} />}
      {detail && detail.source === "Crypto" && (() => {
        const t = detail.trade;
        const charges = (t.entryFee || 0) + (t.tierFee || 0) + (t.exitFee || 0) + (t.fundingFee || 0);
        let live = null;
        if (t.status === "OPEN" && t.lastPrice != null) {
          const k = t.quote === "USDT" ? (cst?.fx?.rate || t.fxEntry || 95.5) : 1;
          const rem = t.remainingQty ?? t.qty;
          const sgn = t.side === "SHORT" ? -1 : 1;
          const feeSide = cst?.params?.feePerSide ?? 0.000472;
          live = (t.bookedPnl || 0) + (t.lastPrice - t.entry) * rem * sgn * k - (t.entryFee || 0) - t.lastPrice * rem * feeSide * k - (t.fundingFee || 0);
        }
        return <MoneyDrawer t={t} charges={charges} live={live}
          lev={cst?.contractSpecs?.[t.symbol]?.maxLeverage} cfg={cst?.config} params={cst?.params}
          onClose={() => setDetail(null)} />;
      })()}

      <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 8 }}>
        Derived live from the trade records (15s poll) — an event appears the moment its timestamp lands on the trade: ENTRY when placed, TIER when a partial books, EXIT when it closes with the reason and net P&L. Crypto trades are the paper validation books; Indian rows carry their real LIVE/PAPER mode badge.
      </div>
    </div>
  );
}
